import type { RecordView } from '@kit/sync/types/record-view';
import { Plus, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { RecordEditor } from '@/entities/shared/record/record-editor';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { useEditorParam } from '@/shared/components/editor-sheet/use-editor-param';
import { EmptyState } from '@/shared/components/empty-state';
import { ListCard } from '@/shared/components/list/list-card';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Button } from '@/shared/ui/button/button';
import { Tabs } from '@/shared/ui/tabs/tabs';
import { TabsList } from '@/shared/ui/tabs/tabs-list';
import { TabsTrigger } from '@/shared/ui/tabs/tabs-trigger';
import { useShoppingList } from '../hooks/use-shopping-list';
import type { ShoppingTab } from '../schemas/shopping-tab';
import { BuyDialog } from './buy-dialog';
import { ShoppingEditor } from './shopping-editor';
import { ShoppingFilters } from './shopping-filters';
import { ShoppingRow } from './shopping-row';
import { ShoppingStats } from './shopping-stats';

export function ShoppingScreen() {
  const { t } = useTranslation();
  const shopping = useShoppingList();
  const { list, filtered, shown, tab, setTab, locations, filtering } = shopping;
  const editor = useEditorParam();
  const [buying, setBuying] = useState<RecordView<ShoppingItem> | null>(null);
  return (
    <Page>
      <PageHeader
        title={t('Shopping')}
        description={t('What is left to buy and what it will cost.')}
        primaryAction={{ label: t('Add'), onClick: () => editor.open('new') }}
      />
      {/* Tabs and filters first, so the list starts high on a phone. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The tab values are the ShoppingTab literals below. */}
        <Tabs value={tab} onValueChange={(value) => setTab(value as ShoppingTab)}>
          <TabsList>
            <TabsTrigger value="pending">{t('To buy')}</TabsTrigger>
            <TabsTrigger value="bought">{t('Bought')}</TabsTrigger>
            <TabsTrigger value="discarded">{t('Discarded')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <ShoppingFilters shopping={shopping} />
      </div>
      <ShoppingStats list={filtered ?? []} />
      {!list ? (
        <ListSkeleton />
      ) : shown.length === 0 && filtering ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('Nothing matches the filter.')}</p>
      ) : shown.length === 0 && tab === 'pending' ? (
        <EmptyState
          icon={ShoppingCart}
          title={t('Nothing pending')}
          description={t(
            'Write down what you are missing with a price estimate to know what completing the kit costs.',
          )}
          action={
            <Button onClick={() => editor.open('new')}>
              <Plus /> {t('Add to the list')}
            </Button>
          }
        />
      ) : shown.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('Nothing here.')}</p>
      ) : (
        <ListCard>
          {shown.map((view) => (
            <ShoppingRow
              key={view.id}
              view={view}
              onOpen={() => editor.open(view.id)}
              onBuy={() => setBuying(view)}
            />
          ))}
        </ListCard>
      )}
      <RecordEditor<ShoppingItem> collection="shopping" id={editor.editing}>
        {(view) => <ShoppingEditor view={view} list={list ?? []} onClose={editor.close} />}
      </RecordEditor>
      {buying && <BuyDialog view={buying} locations={locations} onClose={() => setBuying(null)} />}
    </Page>
  );
}
